import React, { useState, useMemo, useEffect } from 'react'
import styled from 'styled-components'
import { IoCloseCircleSharp, IoClose } from 'react-icons/io5'
import { FaSearch, FaCheck } from 'react-icons/fa'

export interface SelectionItem {
  id: string | number
  label: string
  subtitle?: string
  extra?: string
}

interface SelectionModalProps {
  isOpen: boolean
  title: string
  icon?: React.ReactNode
  items: SelectionItem[]
  selectedIds: (string | number)[]
  onConfirm: (newSelectedIds: (string | number)[]) => void
  onClose: () => void
  allLabel?: string
}

const Backdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 15, 45, 0.85);
  backdrop-filter: blur(8px);
  z-index: 999999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  animation: fadeIn 0.2s ease-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`

const ModalContainer = styled.div`
  background: linear-gradient(180deg, #001f54 0%, #001233 100%);
  border: 1px solid rgba(255, 255, 255, 0.15);
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.6);
  border-radius: 18px;
  width: 100%;
  max-width: 600px;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  font-family: 'Montserrat', sans-serif;
  color: #ffffff;
`

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 24px;
  background: rgba(0, 43, 128, 0.5);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);

  .title-group {
    display: flex;
    align-items: center;
    gap: 12px;

    h3 {
      margin: 0;
      font-size: 17px;
      font-weight: 700;
      color: #ffffff;
      letter-spacing: 0.3px;
    }
  }

  .close-btn {
    background: transparent;
    border: none;
    color: #94a3b8;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: color 0.2s, transform 0.2s;

    &:hover {
      color: #ef4444;
      transform: scale(1.1);
    }
  }
`

const SearchBarWrapper = styled.div`
  padding: 16px 24px 10px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const SearchInputBox = styled.div`
  display: flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  padding: 10px 14px;
  gap: 10px;
  transition: all 0.2s;

  &:focus-within {
    border-color: #3b82f6;
    background: rgba(255, 255, 255, 0.12);
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.25);
  }

  input {
    flex: 1;
    background: transparent;
    border: none;
    outline: none;
    color: #ffffff;
    font-size: 13px;
    font-family: inherit;

    &::placeholder {
      color: #94a3b8;
    }
  }

  .clear-search {
    background: transparent;
    border: none;
    color: #94a3b8;
    cursor: pointer;
    display: flex;
    align-items: center;
    &:hover {
      color: #ffffff;
    }
  }
`

const ActionsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 12px;

  .stats {
    color: #94a3b8;
    strong {
      color: #60a5fa;
    }
  }

  .buttons {
    display: flex;
    gap: 8px;

    button {
      background: rgba(255, 255, 255, 0.08);
      border: 1px solid rgba(255, 255, 255, 0.15);
      color: #ffffff;
      padding: 5px 12px;
      border-radius: 8px;
      font-size: 11px;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.2s;

      &:hover {
        background: rgba(255, 255, 255, 0.18);
      }

      &.select-all {
        color: #a6c10b;
        border-color: rgba(166, 193, 11, 0.4);
        &:hover {
          background: rgba(166, 193, 11, 0.2);
        }
      }

      &.clear-all {
        color: #f87171;
        border-color: rgba(248, 113, 113, 0.4);
        &:hover {
          background: rgba(248, 113, 113, 0.2);
        }
      }
    }
  }
`

const ListScroll = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 8px 24px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 450px;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.2);
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 3px;
  }
`

const ItemCard = styled.div<{ isSelected: boolean }>`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 16px;
  background: ${(props) =>
    props.isSelected ? 'rgba(59, 130, 246, 0.2)' : 'rgba(255, 255, 255, 0.04)'};
  border: 1px solid
    ${(props) =>
      props.isSelected ? '#3b82f6' : 'rgba(255, 255, 255, 0.08)'};
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${(props) =>
      props.isSelected
        ? 'rgba(59, 130, 246, 0.28)'
        : 'rgba(255, 255, 255, 0.08)'};
    border-color: ${(props) =>
      props.isSelected ? '#60a5fa' : 'rgba(255, 255, 255, 0.2)'};
    transform: translateY(-1px);
  }

  .checkbox {
    width: 20px;
    height: 20px;
    border-radius: 6px;
    border: 2px solid
      ${(props) => (props.isSelected ? '#3b82f6' : 'rgba(255, 255, 255, 0.3)')};
    background: ${(props) => (props.isSelected ? '#3b82f6' : 'transparent')};
    display: flex;
    align-items: center;
    justify-content: center;
    color: #ffffff;
    font-size: 11px;
    transition: all 0.2s;
    flex-shrink: 0;
  }

  .info {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 3px;
    overflow: hidden;

    .main-label {
      font-size: 13px;
      font-weight: 600;
      color: ${(props) => (props.isSelected ? '#ffffff' : '#e2e8f0')};
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .sub-row {
      display: flex;
      align-items: center;
      gap: 10px;
      font-size: 11px;
      color: #94a3b8;

      span.extra {
        background: rgba(255, 255, 255, 0.1);
        padding: 1px 6px;
        border-radius: 4px;
        font-size: 10px;
        color: #cbd5e1;
      }
    }
  }
`

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #94a3b8;
  font-size: 13px;
`

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
  background: rgba(0, 20, 60, 0.6);
  border-top: 1px solid rgba(255, 255, 255, 0.1);

  .info-tag {
    font-size: 12px;
    color: #cbd5e1;
    strong {
      color: #38bdf8;
    }
  }

  .btn-group {
    display: flex;
    gap: 10px;

    button {
      padding: 10px 20px;
      border-radius: 10px;
      font-size: 13px;
      font-weight: 600;
      font-family: inherit;
      cursor: pointer;
      transition: all 0.2s;

      &.btn-cancel {
        background: rgba(255, 255, 255, 0.08);
        border: 1px solid rgba(255, 255, 255, 0.2);
        color: #e2e8f0;

        &:hover {
          background: rgba(255, 255, 255, 0.15);
        }
      }

      &.btn-confirm {
        background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
        border: none;
        color: #ffffff;
        box-shadow: 0 4px 14px rgba(37, 99, 235, 0.4);

        &:hover {
          background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
          transform: translateY(-1px);
          box-shadow: 0 6px 20px rgba(37, 99, 235, 0.5);
        }
      }
    }
  }
`

export const SelectionModal: React.FC<SelectionModalProps> = ({
  isOpen,
  title,
  icon,
  items,
  selectedIds,
  onConfirm,
  onClose,
  allLabel = 'Todos',
}) => {
  const [tempSelected, setTempSelected] = useState<(string | number)[]>([])
  const [search, setSearch] = useState('')

  useEffect(() => {
    if (isOpen) {
      setTempSelected(selectedIds)
      setSearch('')
    }
  }, [isOpen, selectedIds])

  const filteredItems = useMemo(() => {
    if (!search.trim()) return items
    const term = search.toLowerCase().trim()
    return items.filter(
      (item) =>
        item.label.toLowerCase().includes(term) ||
        (item.subtitle && item.subtitle.toLowerCase().includes(term)) ||
        (item.extra && item.extra.toLowerCase().includes(term)),
    )
  }, [items, search])

  if (!isOpen) return null

  const handleToggleItem = (id: string | number) => {
    setTempSelected((prev) => {
      if (prev.length === 0) {
        return items.map((i) => i.id).filter((itemNodeId) => String(itemNodeId) !== String(id))
      }
      if (prev.some((selectedId) => String(selectedId) === String(id))) {
        const next = prev.filter((selectedId) => String(selectedId) !== String(id))
        return next.length === 0 ? ['__NONE__'] : next
      }
      const next = prev.filter((selectedId) => selectedId !== '__NONE__').concat(id)
      return next
    })
  }

  const handleSelectAll = () => {
    setTempSelected([])
  }

  const handleClearAll = () => {
    setTempSelected(['__NONE__'])
  }

  const handleConfirm = () => {
    if (tempSelected.includes('__NONE__') && tempSelected.length === 1) {
      onConfirm(['__NONE__'])
    } else {
      onConfirm(tempSelected)
    }
    onClose()
  }

  const selectedCount =
    tempSelected.length === 0
      ? items.length
      : tempSelected.includes('__NONE__')
        ? 0
        : tempSelected.length

  return (
    <Backdrop onClick={onClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <Header>
          <div className='title-group'>
            {icon}
            <h3>{title}</h3>
          </div>
          <button className='close-btn' onClick={onClose}>
            <IoCloseCircleSharp size={24} />
          </button>
        </Header>

        <SearchBarWrapper>
          <SearchInputBox>
            <FaSearch color='#94a3b8' size={14} />
            <input
              type='text'
              placeholder='Pesquisar por nome, código ou documento...'
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
            />
            {search && (
              <button className='clear-search' onClick={() => setSearch('')}>
                <IoClose size={18} />
              </button>
            )}
          </SearchInputBox>

          <ActionsRow>
            <div className='stats'>
              Mostrando <strong>{filteredItems.length}</strong> de {items.length} itens
            </div>
            <div className='buttons'>
              <button className='select-all' type='button' onClick={handleSelectAll}>
                Selecionar Todos
              </button>
              <button className='clear-all' type='button' onClick={handleClearAll}>
                Limpar
              </button>
            </div>
          </ActionsRow>
        </SearchBarWrapper>

        <ListScroll>
          {filteredItems.length === 0 ? (
            <EmptyState>Nenhum item encontrado com o termo pesquisado.</EmptyState>
          ) : (
            filteredItems.map((item) => {
              const isSelected =
                tempSelected.length === 0 ||
                (!tempSelected.includes('__NONE__') &&
                  tempSelected.some((id) => String(id) === String(item.id)))

              return (
                <ItemCard
                  key={item.id}
                  isSelected={isSelected}
                  onClick={() => handleToggleItem(item.id)}
                >
                  <div className='checkbox'>{isSelected && <FaCheck size={10} />}</div>
                  <div className='info'>
                    <div className='main-label'>{item.label}</div>
                    {(item.subtitle || item.extra) && (
                      <div className='sub-row'>
                        {item.subtitle && <span>{item.subtitle}</span>}
                        {item.extra && <span className='extra'>{item.extra}</span>}
                      </div>
                    )}
                  </div>
                </ItemCard>
              )
            })
          )}
        </ListScroll>

        <Footer>
          <div className='info-tag'>
            {tempSelected.length === 0 ? (
              <span>
                Status: <strong>{allLabel} ({items.length})</strong>
              </span>
            ) : tempSelected.includes('__NONE__') ? (
              <span>
                Status: <strong style={{ color: '#f87171' }}>Nenhum selecionado</strong>
              </span>
            ) : (
              <span>
                Selecionados: <strong>{selectedCount}</strong> de {items.length}
              </span>
            )}
          </div>
          <div className='btn-group'>
            <button className='btn-cancel' type='button' onClick={onClose}>
              Cancelar
            </button>
            <button className='btn-confirm' type='button' onClick={handleConfirm}>
              Confirmar Seleção
            </button>
          </div>
        </Footer>
      </ModalContainer>
    </Backdrop>
  )
}
